import { h } from 'preact';
import { useCallback, useEffect, useId, useRef, useState } from 'preact/hooks';
import type { UrlUnfurledData } from 'core_main/pubnub/types';

import UrlPreview from './UrlPreview';
import UrlPreviewCarouselChevron from './UrlPreviewCarouselChevron';
import styles from './urlPreviewCarousel.scss';

interface UrlPreviewsCarouselProps {
  /** Previews sorted by `preferredOrder` (caller responsibility). */
  previews: UrlUnfurledData[];
  direction: 'in' | 'out';
  /** Parent message id so tab/panel DOM ids stay unique when multiple carousels mount. */
  messageId?: string;
}

function sanitizeDomIdSegment(value: string): string {
  return value.trim().replace(/[^a-zA-Z0-9_-]/g, '-');
}

/**
 * Horizontal carousel for multiple URL previews when the server marks every
 * unfurl with `preferredOrder` (KUS-9022). Row height follows the tallest slide
 * because the track uses `align-items: stretch`. Optional prev/next rails flank the
 * viewport only when a control exists so the carousel uses the full row width.
 * Pagination uses a horizontal tab list: ArrowLeft/ArrowRight move one slide at a time
 * (no wrap past ends; Home/End are intentionally not handled here).
 */
const UrlPreviewsCarousel = ({
  previews,
  direction,
  messageId,
}: UrlPreviewsCarouselProps) => {
  const generatedId = useId();
  const idRoot =
    messageId != null && messageId.trim() !== ''
      ? `url-preview-carousel-${sanitizeDomIdSegment(messageId)}`
      : `url-preview-carousel-${generatedId}`;

  const slidePanelId = (i: number) => `${idRoot}-slide-${i}`;
  const slideTabId = (i: number) => `${idRoot}-tab-${i}`;

  const [index, setIndex] = useState(0);
  const slideCount = previews.length;

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(0, slideCount - 1)));
  }, [slideCount]);

  const assignTabRef = useCallback(
    (i: number, el: HTMLButtonElement | null) => {
      tabRefs.current[i] = el;
    },
    [],
  );

  const focusTab = useCallback(
    (nextIndex: number) => {
      if (slideCount < 1) return;
      if (nextIndex < 0 || nextIndex >= slideCount) return;
      setIndex(nextIndex);
      queueMicrotask(() => {
        tabRefs.current[nextIndex]?.focus();
      });
    },
    [slideCount],
  );

  const handleTablistKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (slideCount < 2) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const { target } = e;
      if (
        !target ||
        !(target instanceof HTMLButtonElement) ||
        target.getAttribute('role') !== 'tab'
      ) {
        return;
      }
      const fromIndex = Number(target.dataset.carouselDotIndex);
      if (Number.isNaN(fromIndex)) return;

      let nextIndex: number;
      if (e.key === 'ArrowRight') {
        nextIndex = Math.min(slideCount - 1, fromIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        nextIndex = Math.max(0, fromIndex - 1);
      } else {
        return;
      }

      if (nextIndex === fromIndex) return;
      e.preventDefault();
      focusTab(nextIndex);
    },
    [focusTab, slideCount],
  );

  const slideSharePercent = 100 / slideCount;
  const trackStyle = {
    width: `${slideCount * 100}%`,
    transform: `translateX(-${(index * 100) / slideCount}%)`,
  };
  const slideStyle = {
    width: `${slideSharePercent}%`,
  };

  const showPrev = slideCount > 1 && index > 0;
  const showNext = slideCount > 1 && index < slideCount - 1;

  const goToSlide = useCallback(
    (nextIndex: number) => {
      if (slideCount < 1) return;
      if (nextIndex < 0 || nextIndex >= slideCount) return;
      setIndex(nextIndex);
    },
    [slideCount],
  );

  return (
    <div className={styles.urlPreviewsCarousel} data-direction={direction}>
      <div className={styles.urlPreviewsCarouselMainRow}>
        {showPrev ? (
          <div className={styles.urlPreviewsCarouselRail}>
            <button
              type="button"
              className={`${styles.urlPreviewsCarouselNavButton} ${styles.urlPreviewsCarouselNavButtonPrev}`}
              aria-label="Go to previous URL preview"
              aria-controls={slidePanelId(index - 1)}
              onClick={() => goToSlide(index - 1)}
            >
              <UrlPreviewCarouselChevron direction="left" />
            </button>
          </div>
        ) : null}
        <div className={styles.urlPreviewsCarouselViewport}>
          <div className={styles.urlPreviewsCarouselTrack} style={trackStyle}>
            {previews.map((preview, i) => {
              const isActivePanel = i === index;
              return (
                <div
                  key={`${preview.url}-${i}`}
                  id={slidePanelId(i)}
                  className={styles.urlPreviewsCarouselSlide}
                  role="tabpanel"
                  aria-labelledby={slideTabId(i)}
                  aria-hidden={isActivePanel ? undefined : 'true'}
                  tabIndex={isActivePanel ? 0 : -1}
                  inert={!isActivePanel}
                  style={slideStyle}
                >
                  <UrlPreview
                    preview={preview}
                    direction={direction}
                    className={styles.urlPreviewInCarousel}
                  />
                </div>
              );
            })}
          </div>
        </div>
        {showNext ? (
          <div className={styles.urlPreviewsCarouselRail}>
            <button
              type="button"
              className={`${styles.urlPreviewsCarouselNavButton} ${styles.urlPreviewsCarouselNavButtonNext}`}
              aria-label="Go to next URL preview"
              aria-controls={slidePanelId(index + 1)}
              onClick={() => goToSlide(index + 1)}
            >
              <UrlPreviewCarouselChevron direction="right" />
            </button>
          </div>
        ) : null}
      </div>
      <nav className={styles.urlPreviewsCarouselPagination}>
        <ul
          role="tablist"
          aria-orientation="horizontal"
          className={styles.urlPreviewsCarouselDots}
          onKeyDown={handleTablistKeyDown}
        >
          {previews.map((preview, i) => {
            const isActive = i === index;
            return (
              <li
                key={`${preview.url}-dot-${i}`}
                className={styles.urlPreviewsCarouselDotItem}
                role="none"
              >
                <button
                  type="button"
                  id={slideTabId(i)}
                  ref={(el) => assignTabRef(i, el)}
                  role="tab"
                  className={
                    isActive
                      ? `${styles.urlPreviewsCarouselDot} ${styles.urlPreviewsCarouselDotActive}`
                      : styles.urlPreviewsCarouselDot
                  }
                  aria-selected={isActive ? 'true' : 'false'}
                  aria-controls={slidePanelId(i)}
                  data-carousel-dot-index={String(i)}
                  onClick={() => focusTab(i)}
                >
                  <span className={styles.visuallyHidden}>
                    {`${i + 1} / ${slideCount}`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default UrlPreviewsCarousel;
